import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WideDrawer from '../../components/general/WideDrawer.tsx';
import { cleanDocumentFileName } from '../../lib/TFICLib.ts';
import { User } from '../../api/accessControl/User.ts';
import { DocumentDrawerNavBar } from './components/DocumentDrawerNavBar.tsx';
import { DocumentDrawerView } from './components/DocumentDrawerView.tsx';
import { useDrawerNavigation, useOpenDocument } from './DocumentDrawerProvider.tsx';
import { useDocumentDrawerService } from './hooks/useDocumentDrawerService.ts';
import { SubledgerProvider } from '../../contexts/SubledgerContext.tsx';
import { useDocumentTransitions } from './hooks/useDocumentTransitions.ts';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts.ts';
import type { DocumentSavePayload } from './utils/documentSave.ts';
import type { BillTodoType } from '../../generated/enums.ts';
import PossibleDuplicatesModal from '../../components/specific/PossibleDuplicates/PossibleDuplicatesModal.tsx';
import { LINK_FIXED_ASSETS_FLAG } from '../../pages/app/partner/pages/partnerDashboardPage/components/FixedAssets/linkFixedAssetsFlag.ts';

type NudgeDirection = 'previous' | 'next';

function nudgeBack(element: HTMLElement | null, direction: NudgeDirection) {
    if (!element || typeof element.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const offset = direction === 'next' ? '-10px' : '10px';
    element.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(${offset})`, offset: 0.35 }, { transform: 'translateX(0)' }],
        { duration: 260, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' },
    );
}

export function DocumentDrawer({ currentUser }: { currentUser: User }) {
    const openSession = useOpenDocument();
    const navigation = useDrawerNavigation();
    const contentRef = useRef<HTMLDivElement>(null);
    const [documentWithUnsavedChanges, setDocumentWithUnsavedChanges] = useState<string | null>(null);
    const [saveNudge, setSaveNudge] = useState(0);

    const documentId = openSession?.documentId ?? '';
    const hasUnsavedChanges = documentWithUnsavedChanges === documentId && documentId !== '';

    const setHasUnsavedChanges = useCallback(
        (unsaved: boolean) => setDocumentWithUnsavedChanges(unsaved ? documentId : null),
        [documentId],
    );

    useDocumentTransitions(contentRef);
    const session = useDocumentDrawerService(currentUser);

    const { onSave } = session;
    const onNext = navigation?.onNext;
    const handOffOnSave = openSession?.autoAdvance.onSave ?? false;
    const saveAndNext = useMemo(
        () =>
            onNext
                ? async (payload: DocumentSavePayload) => {
                    const handedOff = await onSave(payload, { handOff: handOffOnSave });
                    if (!handedOff) {
                        onNext();
                    }
                }
                : undefined,
        [onSave, onNext, handOffOnSave],
    );

    const onDismissTodo = useCallback(
        (todoType: BillTodoType) => session.onDismissTodo(todoType, { handOff: handOffOnSave && !hasUnsavedChanges }),
        [session.onDismissTodo, handOffOnSave, hasUnsavedChanges],
    );

    const duplicatesModal = session.possibleDuplicatesModal;
    const guardedDuplicatesModal = useMemo(
        () => ({
            ...duplicatesModal,
            onView: (bill: Parameters<typeof duplicatesModal.onView>[0]) => {
                if (hasUnsavedChanges && bill.document_id !== documentId) {
                    duplicatesModal.onClose();
                    setSaveNudge((count) => count + 1);
                    return;
                }
                duplicatesModal.onView(bill);
            },
        }),
        [duplicatesModal, hasUnsavedChanges, documentId],
    );

    const blockedByUnsavedChanges = useCallback(
        (direction: NudgeDirection) => {
            if (!hasUnsavedChanges) {
                return false;
            }
            nudgeBack(contentRef.current, direction);
            setSaveNudge((count) => count + 1);
            return true;
        },
        [hasUnsavedChanges],
    );

    useEffect(() => {
        const element = contentRef.current;
        if (!session.isOpen || !documentId || !element) {
            return;
        }
        element.focus({ preventScroll: true });
    }, [session.isOpen, documentId]);

    useKeyboardShortcuts({
        shortcuts: [
            {
                keys: ['ArrowLeft'],
                handler: () => {
                    if (blockedByUnsavedChanges('previous')) return;
                    navigation?.onPrevious();
                },
            },
            {
                keys: ['ArrowRight'],
                handler: () => {
                    if (blockedByUnsavedChanges('next')) return;
                    navigation?.onNext();
                },
            },
        ],
        enabled: session.isOpen && navigation !== null && !session.isDocumentLoading,
        ownerRef: contentRef,
    });

    const title = cleanDocumentFileName(
        session.document?.file_name || navigation?.fileName || 'Document',
    );

    return (
        <WideDrawer
            open={session.isOpen}
            width="xl"
            panelClassName="bg-gray-50 rounded-l-2xl border-l border-gray-200"
            overflowClassName="overflow-y-hidden"
            contentClassName="!mt-2 flex min-h-0 flex-col"
            title={
                <DocumentDrawerNavBar
                    title={title}
                    showNavigation={navigation !== null}
                    currentIndex={navigation?.currentIndex ?? -1}
                    totalDocuments={navigation?.total ?? 0}
                    onPrevious={navigation?.onPrevious ?? (() => {})}
                    onNext={navigation?.onNext ?? (() => {})}
                    isDocumentLoading={session.isDocumentLoading}
                    hasUnsavedChanges={hasUnsavedChanges}
                    className="pr-8"
                />
            }
            updateOpenState={(open: boolean) => {
                if (!open) {
                    session.close();
                }
            }}
        >
            {session.isOpen && (
                <SubledgerProvider user={currentUser} clientBusinessId={session.businessId ?? null}>
                    <div ref={contentRef} tabIndex={-1} className="flex min-h-0 flex-1 flex-col focus:outline-none">
                        <DocumentDrawerView
                            document={session.document}
                            isDocumentLoading={session.isDocumentLoading}
                            isDocumentContentLoading={session.isDocumentContentLoading}
                            documentId={documentId}
                            businessId={session.businessId ?? ''}
                            documentLoadError={session.documentError}
                            matchData={session.matchData}
                            glAccounts={session.glAccounts}
                            glAccountsLoading={session.glAccountsLoading}
                            lineItemsNeedingAttention={session.lineItemsNeedingAttention}
                            initialTab={session.initialTab}
                            displayMode={session.displayMode}
                            onSave={session.onSave}
                            onConvertToEuro={session.onConvertToEuro}
                            onSaveAndNext={saveAndNext}
                            saveAndNextOnly={handOffOnSave}
                            onUnmatch={session.onUnmatch}
                            onRecategorize={session.onRecategorize}
                            onDelete={session.onDelete}
                            canArchive={currentUser.flags.getFlag('PARTNER__CAN_ARCHIVE_DOCUMENTS')}
                            canLinkFixedAssets={currentUser.flags.getFlag(LINK_FIXED_ASSETS_FLAG)}
                            hasUnsavedChanges={hasUnsavedChanges}
                            setHasUnsavedChanges={setHasUnsavedChanges}
                            saveNudge={saveNudge}
                            billTodos={session.billTodos}
                            onDismissTodo={onDismissTodo}
                            onShowDuplicates={session.onShowDuplicates}
                        />
                    </div>
                    <PossibleDuplicatesModal {...guardedDuplicatesModal}/>
                </SubledgerProvider>
            )}
        </WideDrawer>
    );
}
